import axios from 'axios'
import MockAdapter from 'axios-mock-adapter'
import {base_url} from '../_common'
import {buildIndex, filterByInx, sort, mockRequests} from './_utils'
import {ResponseError} from './_response'
import middlewares from './_middlewares'
import data from './data/transactions'
import {increase_counter, registered_sections} from './updates'
// import * as clients from './clients'
import * as accounts from './accounts'
import * as projects from './projects'

// =============================================================
// mock axios requests

const path = `${base_url}/transactions`;
let mockAdapter;

export function register_mock() {
  registered_sections['transactions'] = true;
  registered_sections['transactions_type_list'] = true;
  mockAdapter = new MockAdapter(axios);

  mockRequests({
    mockAdapter,
    delay: 500,
    middlewares: middlewares.basic,
    requests: [
      ['get', path+'/:id', ({route, params}) => show(route.id, params)],
      ['post', path+'/validate', ({data}) => validate(data.transaction)],
      ['post', path, ({data}) => store(data.transaction)],
      ['put', path+'/:id', ({route, data}) => update(route.id, data.transaction)],
      ['post', path+'/:id/status', ({route, data}) => update_status(route.id, data.status_code)],
      ['delete', path+'/:id', ({route}) => destroy(route.id)],
      ['get', path+'/available/:project_id', ({route, params}) => available_in_project(route.project_id, params.current_transaction_id)],
      ['post', path+'/scan-serial', ({data}) => scan_serial(data.serial, data.current_transaction_id)],
    ]
  });

  // pass through other requests
  mockAdapter.onAny().passThrough();
}

// =============================================================
// data

const transactions = data.transactions;
const subheads = data.subheads;
const lines = data.lines;
const serials = data.serials;

const template = data.template;

let transactions_inx;
let subheads_inx;
let lines_inx;
let lines_in_inx;
let lines_serials_inx;
let serials_inx;

// =============================================================
// controller helper methods

function rebuild(counters = true) {
  transactions_inx = buildIndex(transactions);
  subheads_inx = buildIndex(subheads);
  lines_inx = buildIndex(lines);
  lines_in_inx = buildIndex(lines, 'in_line_id', true);
  lines_serials_inx = buildIndex(serials, 'line_id', true);
  serials_inx = buildIndex(serials, item => `${item.type}:${item.serial}`, true);

  counters && increase_counter('transactions');
  counters && increase_counter('transactions_type_list');
}
rebuild(false);

export function get_data() {
  const data = transactions.map(transaction => {
    const account_id = projects.get_account_id(transaction.project_id);
    const client_id = accounts.get_client_id(account_id);

    return {
      ...transaction,
      client_id,
      account_id,
    }
  });

  // console.log({transactions, subheads, lines, serials});
  return data;
}

export function get_transactions_type_list() {
  const sorted = sort(transactions, ['date', 'time'], true);
  const list = {
    1: sorted.filter(t => t.status_code === 1),
    2: sorted.filter(t => t.status_code === 2),
    3: sorted.filter(t => t.status_code === 3),
    4: sorted.filter(t => t.status_code === 4),
  };

  return Object.keys(list).reduce((r, status_code) => {
    r[status_code] = {
      count: list[status_code].length,
      recent: list[status_code].map(t => {
        const subhead = subheads[subheads_inx[t.subheads[0]]] || {mo_num: 'Not Found', do_num: 'Not Found'};
        return {
          id: t.id,
          mo_num: subhead.mo_num,
          do_num: subhead.do_num,
        };
      }),
    };
    return r;
  }, {});
}

// =============================================================
// controller CRUD methods

export function show(id, options = {}) {
  if (transactions_inx[id] === undefined) {
    throw new ResponseError(404, {_: ['not_found@transaction']});
  }

  const transaction = transactions[transactions_inx[id]];
  const account_id = projects.get_account_id(transaction.project_id);
  const client_id = accounts.get_client_id(account_id);
  
  const transaction_subheads = filterByInx(transaction.subheads, subheads, subheads_inx);
  const transaction_lines = [];

  transaction_subheads.forEach(subhead => {
    const lines_list = filterByInx(subhead.lines, lines, lines_inx);
    transaction_lines.push(...lines_list);
    subhead.lines_indexes = subhead.lines.map(id => transaction_lines.findIndex(line => line.id == id));
  });

  const result = {
    transaction: {
      head: {
        ...transaction,
        client_id,
        account_id,
      },
      subheads: transaction_subheads,
      lines: transaction_lines,
    }
  };

  if (options.for_edit) {
    result.used_lines = {};
    result.transaction.lines.forEach(line => {
      if ((lines_in_inx[line.id] || []).length) {
        result.used_lines[line.id] = true;
      }
    });
  }

  if (options.validate) {
    try {
      validate({
        head: {
          ...transaction,
          status_code: 2,
        },
      });
    } catch (err) {
      if (err instanceof ResponseError) {
        result['validation_errors'] = err.errors;
      } else {
        throw err;
      }
    }
  }

  return result;
}

export function validate(transaction) {

  // example error structure
  const errors = {
    head: {
      client_id: ['not_found@Client'],
      project_id: ['required'],
      type: ['invalid'],
      date: ['date'],
    },
    subheads: {
      0: {
        mo_num: ['required'],
        site: ['required'],
        _: ['other_subhead_errors'],
      },
      1: {
        source_wh_id: ['required'],
      },
    },
    lines: {
      0: {
        item_id: ['not_found@Item'],
        serials: ['unique@1111\n2222\n333'],
      },
      1: {
        serials: ['required', 'unique@1111\n2222\n333'],
      },
      2: {
        qty: ['not_available'],
        material_condition_comment: ['required'],
      },
    },
    _: ['other_errors'],
  };

  if (transaction.head.type == 'out') {
    errors.subheads[0].site_changed = ['required'];
    errors.subheads[1].site_dest = ['required'];
  }

  if (transaction.head.status_code > 1 && transaction.head.transaction_type == 'test_validation_errors') {
    throw new ResponseError(422, errors);
  }
}

export function store(transaction) {
  validate(transaction);

  transaction.head = template('head', {
    ...transaction.head,
    id: data.new_id('transactions'),
    subheads: [],
  });

  transaction.subheads.forEach(subhead => {
    const lines_indexes = subhead.lines_indexes;

    subhead = template('subhead', {
      ...subhead,
      id: data.new_id('subheads'),
      transaction_id: transaction.head.id,
      lines: [],
    });

    lines_indexes.forEach(i => {
      const qty = parseFloat(transaction.lines[i].qty);

      const line = template('line', {
        ...transaction.lines[i],
        id: data.new_id('lines'),
        subhead_id: subhead.id,
        transaction_id: transaction.head.id,
        qty,
        stock: qty,
      });
      
      lines.push(line);
      subhead.lines.push(line.id);
    });

    subheads.push(subhead);
    transaction.head.subheads.push(subhead.id);
  });

  transactions.push(transaction.head);
  rebuild();
  return {id: transaction.head.id};
}

export function update(id, transaction) {
  const old_transaction = transactions[transactions_inx[id]];

  if (!old_transaction) {
    throw new ResponseError(404, {_: ['not_found@transaction']});
  }

  validate(transaction);

  // build indexes hashes for old transaction
  const hashes = {
    subheads: {},
    lines: {},
  }

  old_transaction.subheads.forEach(subhead_id => {
    hashes.subheads[subhead_id] = subheads_inx[subhead_id];
    subheads[subheads_inx[subhead_id]].lines.forEach(line_id => {
      hashes.lines[line_id] = lines_inx[line_id];
    });
  });

  transaction.head = template('head', {
    ...transaction.head,
    subheads: [],
  });

  transaction.subheads.forEach(subhead => {
    const lines_indexes = subhead.lines_indexes;
    const subhead_i = hashes.subheads[subhead.id];
    const is_new_subhead = subhead_i === undefined;
    delete hashes.subheads[subhead.id];

    subhead = template('subhead', {
      ...subhead,
      id: is_new_subhead ? data.new_id('subheads') : subhead.id,
      transaction_id: transaction.head.id,
      lines: [],
    });

    lines_indexes.forEach(i => {
      const qty = parseFloat(transaction.lines[i].qty);
      const line_i = hashes.lines[transaction.lines[i].id];
      const is_new_line = line_i === undefined;
      delete hashes.lines[transaction.lines[i].id];

      const line = template('line', {
        ...transaction.lines[i],
        id: is_new_line ? data.new_id('lines') : transaction.lines[i].id,
        subhead_id: subhead.id,
        transaction_id: transaction.head.id,
        qty,
        stock: qty,
      });
      
      if (is_new_line) {
        lines.push(line);
      } else {
        lines[line_i] = line;
      }
      subhead.lines.push(line.id);
    });

    if (is_new_subhead) {
      subheads.push(subhead);
    } else {
      subheads[subhead_i] = subhead;
    }
    transaction.head.subheads.push(subhead.id);
  });

  transactions[transactions_inx[id]] = transaction.head;

  // removed subehads
  Object.keys(hashes.subheads).forEach(subhead_id => {
    subheads[hashes.subheads[subhead_id]] = null;
  });
  subheads.splice(0, subheads.length, ...subheads.filter(s => s));

  // removed lines & serials
  Object.keys(hashes.lines).forEach(line_id => {
    lines[hashes.lines[line_id]] = null;
    (lines_serials_inx[line_id] || []).forEach(serial_i => {
      serials[serial_i] = null;
    });
  });
  lines.splice(0, lines.length, ...lines.filter(l => l));
  serials.splice(0, serials.length, ...serials.filter(s => s));

  rebuild();
}

export function update_status(id, status_code) {
  if (transactions_inx[id] === undefined) {
    throw new ResponseError(404, {_: ['not_found@transaction']});
  }

  const transaction = transactions[transactions_inx[id]];

  if (transaction.transaction_type == 'test_validation_errors' && status_code === 4) {
    throw new ResponseError(403, {_: ['unauthorized']});
  }

  if (status_code < 1 || status_code > 4 || status_code <= transaction.status_code) {
    throw new ResponseError(422, {'status_code': ['invalid']});
  }

  try {
    validate({
      head: {
        ...transaction,
        status_code: status_code,
      },
    });
  } catch (err) {
    if (err instanceof ResponseError) {
      throw new ResponseError(422, {status_code: ['validation']});
    } else {
      throw err;
    }
  }

  transaction.status_code = status_code;
  rebuild();
}

export function destroy(id) {
  if (transactions_inx[id] === undefined) {
    throw new ResponseError(404, {_: ['not_found@transaction']});
  }

  const transaction = transactions[transactions_inx[id]];

  if (transaction.status_code === 4) {
    throw new ResponseError(403, {_: ['unauthorized']});
  }

  // remove lines
  filterByInx(transaction.subheads, subheads, subheads_inx).forEach(subhead => {
    subhead.lines.forEach(line_id => {
      lines[lines_inx[line_id]] = null;
    });
  });
  lines.splice(0, lines.length, ...lines.filter(l => l));

  // remove subehads
  transaction.subheads.forEach(subhead_id => {
    subheads[subheads_inx[subhead_id]] = null;
  });
  subheads.splice(0, subheads.length, ...subheads.filter(s => s));
  
  // remove serials
  serials.splice(0, serials.length, ...serials.filter(s => s.transaction_id != id));

  // remove transaction
  transactions.splice(transactions_inx[id], 1);
  rebuild();
}

// eslint-disable-next-line
export function available_in_project(project_id, current_transaction_id) {
  
  // current_transaction_id should be used to exclude out_transaction from stock calculatinos

  const available = transactions.filter(t => {
    return t.project_id == project_id &&
           t.type == 'in' &&
           (t.status_code == 2 || t.status_code == 3);
  });

  return {
    transactions: available.map(t => {
      const transaction = show(t.id).transaction;

      transaction.lines.forEach(line => {
        line.transacted_serials = [];
      });

      return transaction;
    }
  )};
}

// eslint-disable-next-line
export function scan_serial(serial, current_transaction_id) {

  // current_transaction_id should be used to exclude out_transaction from serials

  const in_serials = (serials_inx[`in:${serial}`] || []).map(k => serials[k]);
  const out_serials = (serials_inx[`out:${serial}`] || []).map(k => serials[k]);
  
  if (!in_serials.length) {
    throw new ResponseError(404, {scan: ['not_found@serial']});
  }

  let found;

  in_serials.some(in_serial => {
    const available = !out_serials.some(
      out_serial => lines[lines_inx[out_serial.line_id]].in_line_id == in_serial.line_id
    );

    if (available) {
      const line = lines[lines_inx[in_serial.line_id]];
      found = {
        head: transactions[transactions_inx[line.transaction_id]],
        subhead: subheads[subheads_inx[line.subhead_id]],
        line,
      }
      return true;
    }
  });

  if (found) {
    return found;
  } else {
    throw new ResponseError(422, {scan: ['not_available']});
  }
}
